import { useTranslation } from '@fluxora/i18n';
import { Checkbox } from '@fluxora/ui';

/**
 * Click-wrap consent (E-SF06), rebuilt around the DOCUMENT (E-SF18).
 *
 * ── WHAT CHANGED, AND WHAT DID NOT ─────────────────────────────────────────
 * The customer reads their REAL unsigned contract PDF on this step and gives the
 * three acknowledgements against it. Before E-SF18, the PDF only existed after
 * signing, as a download, and this step showed plain-text EFL/TOS/YRAC blocks
 * badged "PREVIEW — not an official document".
 *
 * ── ⚠️ THIS COMPONENT NO LONGER OWNS THE VIEWER (2026-08-26) ────────────────
 * It used to render `ContractPreview` itself, which forced the document and the
 * acknowledgements to be vertically stacked in one column — and that column was
 * capped at `max-w-2xl`, so the contract was drawn 493px wide on a 1375px screen.
 * On desktop the document now occupies its own full-height reading column beside
 * the signing rail, which is only expressible if the PAGE composes the two. The
 * acknowledgements did not move relative to the document in reading order; the
 * document simply stopped being this component's child. `SignPage` renders both.
 *
 * ⚠️ THE ACKNOWLEDGEMENTS ARE NOT DECORATION AND WERE NOT REMOVED. With the typed
 * name they are the OPERATIVE ESIGN ACT (ADR-0010 D5.6; E38 §11) — the drawn mark
 * corroborates and does not execute, and `signature_event_signer_name_present_check`
 * keeps `signer_name` mandatory at the database level. Showing the real document
 * SATISFIES the review obligation more honestly than three text blocks did; it does
 * not replace the acceptance. Anyone tempted to drop the checkboxes now that "they
 * can see the contract" is proposing to remove the only affirmative act in the flow.
 *
 * The text disclosures also survive, moved INSIDE the preview as its accessible
 * alternative: a PDF in an `<object>` is not dependable for a screen reader, and
 * the funnel must stay completable without one (US-SF18.6).
 *
 * ── NOTHING IS PRE-CHECKED ─────────────────────────────────────────────────
 * Every checkbox starts unchecked. The three disclosure acceptances are the
 * affirmative click-wrap act; the TCPA SMS opt-in is separate and defeasible — a
 * customer may sign without it. No box is defaulted true and none auto-accepts.
 */
export interface ClickwrapConsentProps {
  readonly eflAccepted: boolean;
  readonly onEflAcceptedChange: (value: boolean) => void;
  readonly tosAccepted: boolean;
  readonly onTosAcceptedChange: (value: boolean) => void;
  readonly yracAccepted: boolean;
  readonly onYracAcceptedChange: (value: boolean) => void;
  readonly tcpaSmsConsent: boolean;
  readonly onTcpaSmsConsentChange: (value: boolean) => void;
  readonly errors: { readonly efl?: string; readonly tos?: string; readonly yrac?: string };
}

export function ClickwrapConsent({
  eflAccepted,
  onEflAcceptedChange,
  tosAccepted,
  onTosAcceptedChange,
  yracAccepted,
  onYracAcceptedChange,
  tcpaSmsConsent,
  onTcpaSmsConsentChange,
  errors,
}: ClickwrapConsentProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-4" aria-labelledby="sign-consent-heading">
      <h2 id="sign-consent-heading" className="sr-only">
        {t('shop.sign.consent.heading')}
      </h2>

      {/* The affirmative click-wrap. Each is an explicit, unchecked-by-default act,
          and the copy points at the document the page renders alongside it. */}
      <div className="flex flex-col gap-3 rounded-[14px] border border-border bg-surface p-4">
        <p className="text-sm font-medium text-foreground">
          {t('shop.sign.consent.afterReview')}
        </p>
        <Checkbox
          name="acceptEfl"
          checked={eflAccepted}
          onChange={(e) => onEflAcceptedChange(e.target.checked)}
          label={t('shop.sign.consent.efl.label')}
          hasError={errors.efl !== undefined}
        />
        {errors.efl !== undefined ? (
          <span className="text-xs text-danger">{t(errors.efl)}</span>
        ) : null}
        <Checkbox
          name="acceptTos"
          checked={tosAccepted}
          onChange={(e) => onTosAcceptedChange(e.target.checked)}
          label={t('shop.sign.consent.tos.label')}
          hasError={errors.tos !== undefined}
        />
        {errors.tos !== undefined ? (
          <span className="text-xs text-danger">{t(errors.tos)}</span>
        ) : null}
        <Checkbox
          name="acceptYrac"
          checked={yracAccepted}
          onChange={(e) => onYracAcceptedChange(e.target.checked)}
          label={t('shop.sign.consent.yrac.label')}
          hasError={errors.yrac !== undefined}
        />
        {errors.yrac !== undefined ? (
          <span className="text-xs text-danger">{t(errors.yrac)}</span>
        ) : null}
      </div>

      {/* TCPA SMS opt-in — explicit, separate, unchecked by default (not required). */}
      <div className="flex flex-col gap-1 rounded-md border border-border p-3">
        <Checkbox
          name="tcpaSmsConsent"
          checked={tcpaSmsConsent}
          onChange={(e) => onTcpaSmsConsentChange(e.target.checked)}
          label={t('shop.sign.consent.tcpa.label')}
          description={t('shop.sign.consent.tcpa.copy')}
        />
      </div>
    </section>
  );
}
